import { useState } from 'react';
import { useController } from 'react-hook-form';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';

// Same underlying value as the old comma-separated text field — an array of numbers —
// just a clearer, click-to-toggle interface on top of it. `control`/`name` contract is
// unchanged, so this is a drop-in visual upgrade, not a data-shape change.
const DayPicker = ({ control, name, label, hint, commonValues = [1, 3, 7, 14, 30] }) => {
  const { field } = useController({ control, name });
  const selected = Array.isArray(field.value) ? field.value : [];
  const [customValue, setCustomValue] = useState('');

  const toggle = (day) => {
    if (selected.includes(day)) {
      field.onChange(selected.filter((d) => d !== day));
    } else {
      field.onChange([...selected, day].sort((a, b) => a - b));
    }
  };

  const addCustom = () => {
    const num = parseInt(customValue, 10);
    if (!Number.isNaN(num) && num >= 0 && !selected.includes(num)) {
      field.onChange([...selected, num].sort((a, b) => a - b));
    }
    setCustomValue('');
  };

  const extraSelected = selected.filter((d) => !commonValues.includes(d)).sort((a, b) => a - b);

  return (
    <MDBox mb={3}>
      <MDTypography variant="button" fontWeight="medium" display="block">
        {label}
      </MDTypography>
      {hint && (
        <MDTypography variant="caption" color="secondary" display="block" mb={1}>
          {hint}
        </MDTypography>
      )}
      <MDBox display="flex" flexWrap="wrap" gap={1} alignItems="center" mt={1}>
        {commonValues.map((day) => (
          <Chip
            key={day}
            label={`${day} day${day === 1 ? '' : 's'}`}
            clickable
            color={selected.includes(day) ? 'info' : 'default'}
            variant={selected.includes(day) ? 'filled' : 'outlined'}
            onClick={() => toggle(day)}
          />
        ))}
        {extraSelected.map((day) => (
          <Chip
            key={day}
            label={`${day} day${day === 1 ? '' : 's'}`}
            color="info"
            onDelete={() => toggle(day)}
          />
        ))}
        <TextField
          size="small"
          placeholder="Custom"
          value={customValue}
          onChange={(e) => setCustomValue(e.target.value.replace(/\D/g, ''))}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addCustom();
            }
          }}
          sx={{ width: 90 }}
        />
        <MDButton size="small" variant="outlined" color="info" onClick={addCustom}>
          Add
        </MDButton>
      </MDBox>
      {selected.length === 0 && (
        <MDTypography variant="caption" color="text" display="block" mt={0.75}>
          No days selected — this reminder won&apos;t send.
        </MDTypography>
      )}
    </MDBox>
  );
};

export default DayPicker;
